const camelCase = require('lodash/camelCase')
const escapeRegExp = require('lodash/escapeRegExp')

module.exports = class Language {

  /**
   * Sets up Language module
   * @param {object} config
   */
  constructor(config = {}) {
    this.packs = {}

    if (config.hasOwnProperty('packs')) {
      this.loadPacks(config.packs)
    }

    this.defaultLanguage = config.defaultLanguage || 'en'

    this.setLanguage(this.defaultLanguage)
  }

  /**
   * Load language packs passed from config
   * @param  {object} packs - Object containing language packs set in config
   * @return {undefined}
   */
  loadPacks(packs) {
    for (let key in packs) {
      if (this.validatePack(packs[key], key)) {
        this.addPack(packs[key])
      }
    }
  }

  /**
   * Validate Language pack is valid and prevents duplicates
   * @param  {object} pack - Language pack object
   * @param  {string} key  - Language pack key from import for logging
   * @return {boolean}
   */
  validatePack(pack, key) {
    let valid = true

    if (!pack.hasOwnProperty('key')) {
      console.warn(`Language pack '${key}' has no key`)
      valid = false

    } else if (!pack.key || !(pack.key instanceof String || typeof pack.key === 'string')) {
      console.warn(`Language pack '${key}' has invalid key`)
      valid = false
    } else if (this.packs.hasOwnProperty(pack.key)) {
      console.warn(`Language pack '${key}' has already been loaded`)
      valid = false
    }

    if (!pack.hasOwnProperty('volcabulary')) {
      console.warn(`Language pack '${key}' has no volcabulary`)
      valid = false
    } else if (pack !== Object(pack)) {
      console.warn(`Language pack '${key}' has invalid volcabulary`)
      valid = false
    }

    return valid

  }

  /**
   * Adds language pack to language module
   * @param {object} pack - Language pack object
   */
  addPack(pack) {
    this.packs[pack.key] = pack
  }

  /**
   * Set language to use
   * @param {string} language - Language pack key
   * @return {undefined}
   */
  setLanguage(language) {
    this.language = language

    if (!this.packs.hasOwnProperty(language)) {
      console.warn(`No language pack for '${language}'`)
    }
  }

  /**
   * Get volcabulary phrase from dot annotated key
   * @param  {string} dotKey - Volcabulary key
   * @param  {string} [language=null] - Language pack to use if not current set
   *                                    language pack
   * @param  {Array}  [previous=[]]   - History tracker for iterating over
   *                                    extended language packs to avoid
   *                                    infinite loops
   * @return {string}
   */
  get(dotKey, language = null, previous = []) {
    let keys = dotKey.split('.')

    language = language || this.language

    if (previous.includes(language)) {
      console.warn('Infinite language inheritance loop', previous)
      return dotKey
    }

    if (!this.packs.hasOwnProperty(language)) {
      console.warn(`No language pack for '${language}'`)
      return dotKey
    }

    let pack = this.packs[language]
    let inherits = pack.extends
    let current = pack.volcabulary

    for (let key of keys) {
      if (!current.hasOwnProperty(camelCase(key))) {
        if (inherits) {
          return this.get(dotKey, inherits, previous.concat(language))
        }
        return dotKey
      }

      current = current[camelCase(key)]
    }

    if (typeof current !== 'string') {
      if (inherits) {
        return this.get(dotKey, inherits, previous.concat(language))
      }
      return dotKey
    }

    let first_language = previous[0] || language
    return this.processTerms(current, first_language)

  }

  /**
   * Processes volcabulary and adds appropiate terms
   * @param  {string} phrase   - String retrieved from volcabulary
   * @param  {[type]} language - Language to use
   * @return {string}
   */
  processTerms(phrase, language) {

    if (!this.packs.hasOwnProperty(language)) {
      return phrase
    }
    let pack = this.packs[language]

    let regex = /:\{(.*?)\}/g
    let match

    let response = phrase;

    while (match = regex.exec(phrase)) {
      let placeholder = match[0]
      let term = match[1]

      let term_value = this.term(term, language);

      response = response.replace(new RegExp(escapeRegExp(placeholder)), term_value)
    }

    return response

  }

  /**
   * Get term from language pack
   * @param  {string} term            - Term key to find
   * @param  {string} [language=null] - Language pack to use if not current set
   *                                    language pack
   * @param  {Array}  [previous=[]]   - History tracker for iterating over
   *                                    extended language packs to avoid
   *                                    infinite loops
   * @return {string}
   */
  term(term, language = null, previous = []) {

    // Use set language if no override
    language = language || this.language

    // Prevent infinite loop
    if (previous.includes(language)) {
      console.warn('Infinite language inheritance loop', previous)
      return term
    }

    // Check language pack exists
    if (!this.packs.hasOwnProperty(language)) {
      console.warn(`No language pack for '${language}'`)
      return term
    }

    let pack = this.packs[language]
    let parent = pack.extends

    // Check if language pack contains term, if not redo on parent language pack
    if (!pack.terms || !pack.terms[term]) {
      if (parent) {
        return this.term(term, parent, previous.concat(language))
      }

      console.warn(`Could not find term '${term}' in language pack '${previous[0] || language}'`)

      return term
    }

    // Return term
    return pack.terms[term]

  }
}